import { Injectable } from '@angular/core';
import { OfflineStateService } from './offline-state.service';

export interface PendingRequest {
  id: string;
  method: string;
  url: string;
  body: unknown;
  headers: Record<string, string>;
  createdAt: string;
  retryCount: number;
  status: 'PENDING' | 'SYNCED' | 'FAILED';
}

const STORAGE_KEY_PREFIX = 'sf_pending_queue';

/**
 * Queues HTTP mutations (POST/PUT/DELETE) made while offline.
 * Uses localStorage for persistence (survives app restart).
 * Scoped by companyId to prevent cross-company mutation replay.
 */
@Injectable({ providedIn: 'root' })
export class PendingQueueService {

  constructor(private offlineState: OfflineStateService) {}

  private get storageKey(): string {
    const companyId = localStorage.getItem('selectedCompanyId');
    return companyId ? `${STORAGE_KEY_PREFIX}_${companyId}` : STORAGE_KEY_PREFIX;
  }

  enqueue(method: string, url: string, body: unknown, headers: Record<string, string>): PendingRequest {
    const entry: PendingRequest = {
      id: crypto.randomUUID(),
      method,
      url,
      body,
      headers,
      createdAt: new Date().toISOString(),
      retryCount: 0,
      status: 'PENDING',
    };
    const queue = this.getAll();
    queue.push(entry);
    this.save(queue);
    return entry;
  }

  getAll(): PendingRequest[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  getPending(): PendingRequest[] {
    return this.getAll().filter(r => r.status === 'PENDING');
  }

  count(): number {
    return this.getPending().length;
  }

  markSynced(id: string): void {
    const queue = this.getAll();
    const entry = queue.find(r => r.id === id);
    if (entry) {
      entry.status = 'SYNCED';
      this.save(queue);
    }
  }

  markFailed(id: string): void {
    const queue = this.getAll();
    const entry = queue.find(r => r.id === id);
    if (entry) {
      entry.status = 'FAILED';
      entry.retryCount++;
      this.save(queue);
    }
  }

  remove(id: string): void {
    const queue = this.getAll().filter(r => r.id !== id);
    this.save(queue);
  }

  clearSynced(): void {
    const queue = this.getAll().filter(r => r.status !== 'SYNCED');
    this.save(queue);
  }

  clear(): void {
    localStorage.removeItem(this.storageKey);
  }

  private save(queue: PendingRequest[]): void {
    localStorage.setItem(this.storageKey, JSON.stringify(queue));
  }
}
